import { useForm } from "@inertiajs/react"
import { useEffect } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import type { JobType, WorkspaceLevel } from "@/types/bids"

/** Create/edit a level's structure (name + quote unit). The two numbers —
 *  quoted price and target GM — edit inline on the cards, where repricing
 *  actually happens. */
export function LevelDialog({
  open,
  onOpenChange,
  category,
  categoryLabel,
  addUrl,
  level,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  category: JobType
  categoryLabel: string
  addUrl: string
  level: WorkspaceLevel | null
}) {
  const form = useForm({
    bid_level: { category, name: "", price_unit: "sf" as "sf" | "sy" },
  })

  useEffect(() => {
    if (!open) return
    form.setData("bid_level", {
      category,
      name: level?.name ?? "",
      price_unit: level?.price_unit ?? (category === "carpet" ? "sy" : "sf"),
    })
    form.clearErrors()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed on open only
  }, [open, level, category])

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const opts = { preserveScroll: true, onSuccess: () => onOpenChange(false) }
    if (level) form.patch(level.urls.update, opts)
    else form.post(addUrl, opts)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm" data-testid="level-dialog">
        <DialogHeader>
          <DialogTitle>{level ? `Edit ${level.name}` : `Add ${categoryLabel} level`}</DialogTitle>
          <DialogDescription>
            One quoted price per level — every product in it inherits the price; GM is computed per
            product so a hot-cost item can&apos;t hide.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex flex-col gap-1">
            <label htmlFor="level-name">Name</label>
            <Input
              id="level-name"
              value={form.data.bid_level.name}
              onChange={(e) => form.setData("bid_level", { ...form.data.bid_level, name: e.target.value })}
              placeholder={category === "carpet" ? "Standard · UP Level 1 · …" : "Level 1 · Level 2 · …"}
              data-testid="level-name"
            />
            {form.errors["bid_level.name" as keyof typeof form.errors] && (
              <p className="text-xs text-danger-display">
                {form.errors["bid_level.name" as keyof typeof form.errors]}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="level-unit">Quoted per</label>
            <Select
              id="level-unit"
              value={form.data.bid_level.price_unit}
              onChange={(e) =>
                form.setData("bid_level", {
                  ...form.data.bid_level,
                  price_unit: e.target.value as "sf" | "sy",
                })
              }
              data-testid="level-unit-select"
            >
              <option value="sf">Square foot (SF)</option>
              <option value="sy">Square yard (SY)</option>
            </Select>
          </div>
          <div className="modal-footer">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={form.processing || !form.data.bid_level.name.trim()}>
              {level ? "Save level" : "Add level"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
